import {
    DetailsMenuPosition,
    HorizontalDetailsMenuPosition,
    VerticalDetailsMenuPosition
} from "@/app/features/shared/media-view/types/types";
import { memo, MouseEvent, useCallback, useMemo, useState } from "react";
import { Box, Collapse, IconButton, Slide, Stack, Tooltip } from "@mui/material";

import sharedStyles from './media-view-shared.module.css';
import clsx from "clsx";
import { CancelOutlined, InfoOutlined, Loupe, OpenInFull } from "@mui/icons-material";
import Typography from "@mui/material/Typography";

interface DetailsMenuProps {
    imageMeta?: {
        name?: string;
        caption?: string;
    };
    position: DetailsMenuPosition;
    magnifierEnabled?: boolean;
    isMagnifying?: boolean;
    setIsMagnifying?: (isMagnifying: boolean) => void;
    onOpenInFull?: () => void;
}

const ICON_SIZE = 32;

const DetailsMenu = memo(function DetailsMenu({
    imageMeta,
    position,
    magnifierEnabled = false,
    isMagnifying,
    setIsMagnifying,
    onOpenInFull,
}: DetailsMenuProps) {
    const [isImageMetaExpanded, setIsImageMetaExpanded] = useState(false);

    const [verticalPosition, horizontalPosition] = useMemo(() => {
        const positions = position.split(" ");
        const vertical = positions[0] as VerticalDetailsMenuPosition;
        const horizontal = positions[1] as HorizontalDetailsMenuPosition;
        return [vertical, horizontal] as const;
    }, [position]);

    const hideActionButtons = isMagnifying || isImageMetaExpanded;
    const canShowImageMeta = Boolean(imageMeta?.name || imageMeta?.caption);

    const stopEvent = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
    }, []);

    const onShowImageInfo = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        stopEvent(event);
        setIsImageMetaExpanded(true);
    }, [stopEvent]);

    const onHideImageInfo = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        stopEvent(event);
        setIsImageMetaExpanded(false);
    }, [stopEvent]);

    const onEnableMagnifier = useCallback(() => {
        setIsMagnifying?.(true);
    }, [setIsMagnifying]);

    const onDisableMagnifier = useCallback(() => {
        setIsMagnifying?.(false);
    }, [setIsMagnifying]);

    const onOpenInFullView = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        stopEvent(event);
        onOpenInFull?.();
    }, [onOpenInFull, stopEvent]);

    return (
        <Box
            className={clsx(sharedStyles.detailMenuContainer, 'signature-border')}
            sx={{
                [verticalPosition]: 8,
                [horizontalPosition]: 8,
            }}
        >
            <Stack
                direction="row"
                spacing={0.5}
                flexDirection={horizontalPosition === 'right' ? "row" : "row-reverse"}
                sx={{ minWidth: 0, maxWidth: '100%' }}
            >
                {magnifierEnabled && (
                    <Collapse in={!hideActionButtons} orientation="horizontal" timeout={200} unmountOnExit>
                        <Tooltip title="Lupe aktivieren">
                            <IconButton
                                className={sharedStyles.squaredButton}
                                aria-label="Lupe aktivieren"
                                color="primary"
                                onClick={onEnableMagnifier}
                            >
                                <Loupe sx={{ fontSize: ICON_SIZE }}/>
                            </IconButton>
                        </Tooltip>
                    </Collapse>
                )}

                {canShowImageMeta && (
                    <>
                        <Collapse
                            in={isImageMetaExpanded}
                            orientation="horizontal"
                            timeout={200}
                            unmountOnExit
                            sx={{ minWidth: 0, maxWidth: '100%' }}
                        >
                            <Box className={sharedStyles.imageMetaContainer}>
                                <Slide
                                    direction={horizontalPosition === 'right' ? 'left' : 'right'}
                                    in={isImageMetaExpanded}
                                    timeout={{ enter: 260, exit: 140 }}
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    <Stack spacing={1} sx={{ p: 2, minWidth: 0 }}>
                                        {imageMeta?.name && (
                                            <Typography
                                                variant="h4"
                                                color="text.secondary"
                                                textAlign={horizontalPosition}
                                                sx={{ mb: 0, overflowWrap: 'anywhere' }}
                                            >
                                                {imageMeta?.name}
                                            </Typography>
                                        )}
                                        {imageMeta?.caption && (
                                            <Typography
                                                variant="body1"
                                                textAlign={horizontalPosition}
                                                sx={{ overflowWrap: 'anywhere' }}
                                            >
                                                {imageMeta?.caption}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Slide>
                            </Box>
                        </Collapse>
                        <Collapse in={!hideActionButtons} orientation="horizontal" timeout={200} unmountOnExit>
                            <Tooltip title="Bildinfo anzeigen">
                                <IconButton
                                    className={sharedStyles.squaredButton}
                                    aria-label="Bildinfo anzeigen"
                                    color="primary"
                                    onClick={onShowImageInfo}
                                >
                                    <InfoOutlined sx={{ fontSize: ICON_SIZE }}/>
                                </IconButton>
                            </Tooltip>
                        </Collapse>
                    </>
                )}
                <Collapse
                    in={Boolean(onOpenInFull) && !hideActionButtons}
                    orientation="horizontal"
                    timeout={200}
                    unmountOnExit
                >
                    <Tooltip title="Im Vollbild öffnen">
                        <IconButton
                            className={sharedStyles.squaredButton}
                            aria-label="Im Vollbild öffnen"
                            color="primary"
                            onClick={onOpenInFullView}
                        >
                            <OpenInFull sx={{ fontSize: ICON_SIZE }}/>
                        </IconButton>
                    </Tooltip>
                </Collapse>
                <Collapse in={isMagnifying} orientation="horizontal" timeout={200} unmountOnExit>
                    <Tooltip title="Lupe deaktivieren">
                        <IconButton
                            aria-label="Lupe deaktivieren"
                            color="error"
                            onClick={onDisableMagnifier}
                        >
                            <CancelOutlined sx={{ fontSize: 36 }}/>
                        </IconButton>
                    </Tooltip>
                </Collapse>
                <Collapse in={isImageMetaExpanded} orientation="horizontal" timeout={200} unmountOnExit>
                    <Tooltip title="Bildinfo verstecken">
                        <IconButton
                            className={sharedStyles.squaredButton}
                            aria-label="Bildinfo verstecken"
                            color="error"
                            onClick={onHideImageInfo}
                        >
                            <CancelOutlined sx={{ fontSize: 36 }}/>
                        </IconButton>
                    </Tooltip>
                </Collapse>
            </Stack>
        </Box>
    )
})

DetailsMenu.displayName = "DetailsMenu";
export default DetailsMenu;
